import Api from "../../services/Api.ts";
import {useEffect, useState} from "react";
import { useAtom, useSetAtom } from 'jotai';
import {categoriesAtom, matchingClientsAtom, selectedDepartmentsAtom} from '../../jotai_state';
import TooFewClients from "../../components/TooFewClients/TooFewClients.tsx";
import PercentBar from "../../components/Bars/PercentBar.tsx";

interface ReadinessQuestion {
  name: string;
  values: number[]
}

interface ReadinessArea {
  name: string;
  questions: Array<ReadinessQuestion>;
}

interface CategoryInterface {
  id: string;
  name: string;
  count: number;
  readiness: Array<ReadinessArea>;
}

export default function Readiness() {
  const api = Api.getInstance();
  const [tooFewClients, setTooFewClients] = useState<number|undefined>(undefined);
  const [readinessCategories, setReadinessCategories] = useState<Array<CategoryInterface>>([]);
  const [categories] = useAtom(categoriesAtom);
  const [selectedDepartments] = useAtom(selectedDepartmentsAtom);
  const setMatchingClients = useSetAtom(matchingClientsAtom);
  const [loaded, setLoaded] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    getData();
  }, [categories, selectedDepartments]);

  const getData = () => {
    const data = {
      categories: categories,
      departments: selectedDepartments,
    }

    api.post('customer/sprint_conditions', data).then((response) => {
      if (response.json.data.tooFewClients !== undefined) {
        setTooFewClients(response.json.data.tooFewClients)
      } else {
        setMatchingClients(response.json.data.matchingClients);
        setReadinessCategories(response.json.data.categories)
        setTooFewClients(undefined)
      }
      setLoaded(true);
    }).catch(error => {
      setError('Unknown error (http status ' + error.response.status + ')')
      setLoaded(true)
    })
  }

  const renderCategory = (category: CategoryInterface) => {
    if (!category.readiness || category.readiness.length === 0) {
      return null;
    }
    return (
      <div key={"category_" + category.id} className="mt-4">
        <div className="z-h3">{category.name} ({category.count} answers)</div>
        {renderReadiness(category)}
      </div>
    )
  }

  const renderReadiness = (category:CategoryInterface) => {

    return (
      <div className="mt-4">

        <div className="z-box large white">

          {
            category.readiness.map((area, index) => {
              return (
                <div key={"readiness_" + category.id + "_" + index} className="mb-6">
                  <div className="z-title2">{area.name}</div>
                  {
                    area.questions.map((question, i2) => (
                      <div key={"question_" + category.id + "_" + index + "_" + i2}
                           className="mt-4">
                        <div className="mb-2 z-body1 text-gray-1000!">{question.name}</div>
                        <PercentBar values={question.values} texts={["%", "%", "%", "%"]} showZero={false}
                                    height="26px" width="50%"/>
                      </div>
                    ))
                  }
                </div>
                )
              })
            }

          <div className="flex items-center mt-6">
            <div className="h-3 w-3 rounded-full bg-aquamarine-600 "/>
            <div className="ml-2 z-body2">Fully agree</div>
            <div className="ml-4 h-3 w-3  rounded-full bg-orange-300"/>
            <div className="ml-2 z-body2">Agree to a large extent</div>
            <div className="ml-4 h-3 w-3 rounded-full bg-orange-500"/>
            <div className="ml-2 z-body2">Agree to some extent</div>
            <div className="ml-4 h-3 w-3 rounded-full bg-cherry-600"/>
            <div className="ml-2 z-body2">Disagree</div>
          </div>
        </div>

      </div>
    )
  }

  if (!loaded) {
    return <div>Loading data...</div>
  }
  if (error.length > 0) {
    return <div>{error}</div>
  }
  if (tooFewClients !== undefined) {
    return <TooFewClients tooFewClients={tooFewClients}/>
  }

  return (
    <>
      <div className="z-h2">Readiness</div>
      <div className="z-body1 mt-4 text-gray-1000!">
        How well the organisation understands the initiative, wants to work on it and has room to do so -
        the foundation the initiative rests on.
      </div>
      {
        readinessCategories.map(category => renderCategory(category))
      }
      {
        readinessCategories.length === 0 &&
        <p className="mt-4 z-body1">No readiness questions defined</p>
      }
    </>
  )
}